import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, Popover, Typography } from '@mui/material';

import { SideMenuItem } from './config';

interface Props {
  anchorEl: HTMLElement | null;
  submenus: SideMenuItem[];
  onClose: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const LABEL_SX = {
  color: 'grey.800',
  fontSize: '0.8125rem',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 1.6,
  whiteSpace: 'nowrap' as const,
};

const StatisticsHoverPopover: FC<Props> = ({
  anchorEl,
  submenus,
  onClose,
  onMouseEnter,
  onMouseLeave,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);

  const toggleGroup = (label: string) => {
    setCollapsedGroups((prev) =>
      prev.includes(label) ? prev.filter((g) => g !== label) : [...prev, label],
    );
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      disableRestoreFocus
      disableAutoFocus
      disableEnforceFocus
      sx={{ pointerEvents: 'none' }}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: '0.75rem',
          bgcolor: '#fefefe',
          boxShadow: '0px 1px 8px 0px rgba(0, 0, 0, 0.15)',
          ml: '1rem',
          pointerEvents: 'auto',
          minWidth: '14.75rem',
        },
        onMouseEnter,
        onMouseLeave,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', p: '0.5rem', gap: '0.25rem' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            px: '0.375rem',
            py: '0.25rem',
            mb: '0.25rem',
          }}
        >
          <KeyboardArrowDownIcon sx={{ fontSize: '1rem', color: '#615E6F' }} />
          <Typography sx={LABEL_SX}>{t('Statistics')}</Typography>
        </Box>

        {submenus.map((org) => (
          <Box key={org.label}>
            <Box
              sx={{
                px: '0.375rem',
                py: '0.125rem',
                borderRadius: '1rem',
                bgcolor: 'grey.50',
                mb: '0.25rem',
              }}
            >
              <Typography sx={LABEL_SX}>{org.label}</Typography>
            </Box>

            {org.submenus?.map((company) => {
              const isOpen = !collapsedGroups.includes(company.label);
              return (
                <Box key={company.label}>
                  <Box
                    onClick={() => toggleGroup(company.label)}
                    data-testid={`stats-group-${company.label.toLowerCase().replace(/\s+/g, '-')}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      px: '0.375rem',
                      py: '0.125rem',
                      borderRadius: '1rem',
                      bgcolor: 'grey.50',
                      cursor: 'pointer',
                      mb: '0.125rem',
                      '&:hover': { bgcolor: 'grey.100' },
                    }}
                  >
                    <Typography sx={{ ...LABEL_SX, flex: 1 }}>{company.label}</Typography>
                    {isOpen ? (
                      <KeyboardArrowDownIcon sx={{ fontSize: '0.875rem', color: '#615E6F' }} />
                    ) : (
                      <KeyboardArrowRightIcon sx={{ fontSize: '0.875rem', color: '#615E6F' }} />
                    )}
                  </Box>

                  {isOpen &&
                    company.submenus?.map((mall) => (
                      <Box
                        key={mall.label}
                        onClick={() => mall.path && handleNavigate(mall.path)}
                        data-testid={`stats-nav-${mall.label.toLowerCase().replace(/\s+/g, '-')}`}
                        sx={{
                          pl: '1.25rem',
                          py: '0.125rem',
                          cursor: mall.path ? 'pointer' : 'default',
                          borderRadius: '0.375rem',
                          '&:hover': mall.path ? { bgcolor: 'grey.100' } : {},
                        }}
                      >
                        <Typography sx={LABEL_SX}>{mall.label}</Typography>
                      </Box>
                    ))}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Popover>
  );
};

export default StatisticsHoverPopover;
